import React from 'react';
import type { FunnelInsightEdgeMetric } from '../types';
import { ANALYTICS_CANVAS_TEST_IDS } from '../testIds';
import { formatPct } from '../statTierCopy';
import { PEOPLE_ABSENCE_LABEL, unitNoun, useFunnelUnit } from '../funnelUnit';
import { edgeFlowForUnit } from '../unitMetrics';

interface FunnelEdgeMetricLabelProps {
  metric: FunnelInsightEdgeMetric;
  onClick?: () => void;
  /** Spec D D1b (E1): append the back/jump split to the primary count. */
  detailed?: boolean;
}

/**
 * Canvas rework D-C3 + funnel-truth F2 (FT-AC-090): the primary figure is the
 * page unit's distinct count — `uniqueJourneys` labelled «visits», or the F1
 * person twin `uniquePeople` labelled «people» — with its share of the source
 * screen's per-unit visitors, so the on-canvas arithmetic closes (edges +
 * stub ≈ visitors). The unit noun lives in the VISIBLE label (D5, FT-AC-085);
 * the pre-F2 «people» caption over journey numbers was the program's recorded
 * mislabel. Raw moves stay in the tooltip. A people-mode edge without the
 * person twin renders the D7 typed absence — never the visits number
 * relabelled (FT-AC-087).
 */
export const FunnelEdgeMetricLabel: React.FC<FunnelEdgeMetricLabelProps> = ({ metric, onClick, detailed = false }) => {
  const unit = useFunnelUnit();
  const flow = edgeFlowForUnit(metric, unit);
  const noun = unitNoun(unit);
  const share = typeof flow.share === 'number' && Number.isFinite(flow.share)
    ? formatPct(flow.share)
    : undefined;
  const tooltip = flow.available
    ? `${flow.count} ${noun} (${metric.transitionCount} moves`
      + `${share ? ` · ${share} of source visitors` : ''})`
      + ` · forward ${metric.forwardTransitionCount} · back ${metric.backTransitionCount} · jump ${metric.jumpTransitionCount}`
    : PEOPLE_ABSENCE_LABEL;

  return (
    <button
      type="button"
      data-testid={ANALYTICS_CANVAS_TEST_IDS.edgeLabel(metric.canvasTransitionId)}
      title={tooltip}
      onClick={(event) => {
        event.stopPropagation();
        onClick?.();
      }}
      style={{
        minWidth: 74,
        padding: '4px 8px',
        borderRadius: 6,
        background: 'rgba(255, 255, 255, 0.96)',
        border: '1px solid rgba(15, 23, 42, 0.14)',
        color: '#111827',
        boxShadow: '0 4px 12px rgba(15, 23, 42, 0.14)',
        fontSize: 13,
        lineHeight: 1.2,
        textAlign: 'center',
        pointerEvents: onClick ? 'auto' : 'none',
        cursor: onClick ? 'pointer' : 'default',
        whiteSpace: 'nowrap',
      }}
    >
      {flow.available ? (
        <>
          <strong>{(flow.count as number).toLocaleString('en-US')}</strong>
          <span style={{ color: '#64748b', marginLeft: 4 }}>
            {noun}
            {share ? ` · ${share}` : ''}
          </span>
        </>
      ) : (
        // D7 typed absence: person twin missing on this edge (old cache).
        <span style={{ color: '#64748b' }}>n/a · people</span>
      )}
      {detailed && metric.backTransitionCount > 0 && (
        <span style={{ color: '#ed6c02', marginLeft: 5 }}>↩ {metric.backTransitionCount}</span>
      )}
      {detailed && metric.jumpTransitionCount > 0 && (
        <span style={{ color: '#0288d1', marginLeft: 5 }}>⤼ {metric.jumpTransitionCount}</span>
      )}
    </button>
  );
};
